/**
 * Edit a Hide and Seek game's location plan: the places the admin intends to
 * play. Each planned location's maps download to every player on joining, so a
 * round set there shows its map straight away.
 *
 * Rows that are still being chosen (no area yet) are kept here and left out of
 * `onChange`, so the plan the parent holds is always complete.
 */

import { useState } from 'react';
import { HNS_LOCATION_PLAN_MAX, type GameLocation } from '@swtorbit/shared-socket-events';
import { HnsLocationPicker } from './HnsLocationPicker';

interface HnsLocationPlanEditorProps {
  value: GameLocation[];
  onChange: (plan: GameLocation[]) => void;
  disabled?: boolean;
}

export function HnsLocationPlanEditor({ value, onChange, disabled }: HnsLocationPlanEditorProps) {
  const [rows, setRows] = useState<Array<GameLocation | null>>(value);

  const update = (next: Array<GameLocation | null>) => {
    setRows(next);
    onChange(next.filter((row): row is GameLocation => row !== null));
  };

  return (
    <div className="d-flex flex-column gap-xs">
      {rows.map((row, index) => (
        <div key={index} className="d-flex align-items-start gap-xs">
          <div className="flex-1">
            <HnsLocationPicker
              idPrefix={`hns-plan-${index}`}
              value={row}
              disabled={disabled}
              onChange={(location) => update(rows.map((r, i) => (i === index ? location : r)))}
            />
          </div>
          <button
            type="button"
            className="btn btn--outline btn--slate"
            disabled={disabled}
            onClick={() => update(rows.filter((_, i) => i !== index))}
            aria-label="Remove location"
          >
            ✕
          </button>
        </div>
      ))}
      {rows.length < HNS_LOCATION_PLAN_MAX && (
        <button type="button" className="btn btn--outline btn--slate" disabled={disabled} onClick={() => update([...rows, null])}>
          + Add location
        </button>
      )}
    </div>
  );
}
